import Stats from '../../Components/Stats'
import React, { useMemo, useState, useEffect, useRef } from "react";
import CustomTable from '../../Components/CustomTable';
import { getBookingList, downloadBookingInvoice } from '../../api';
import { Loader } from "../../Components/Loader";
import moment from "moment";
import { ToastMsg } from '../../Components/ToastMsg';

const normalizeBookings = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    return {
      id:item?.id||"NA",
      bookingId: item.booking_id  || "N/A",
      name: item.user_details 
        ? `${item.user_details.first_name} ${item.user_details.last_name}` 
        : "N/A",
      email: item.user_details?.email || "N/A",
      phone: item.user_details 
        ? `+${item.user_details.phone_country_code} ${item.user_details.phone_number}` 
        : "N/A",
      carModel: item.car_details 
        ? `${item.car_details.brand} ${item.car_details.model}` 
        : "N/A",
      tripStart: item.start_date 
        ? moment(item.start_date).format("DD/MM/YYYY, HH:mm") 
        : "N/A",
      tripEnd: item.end_date 
        ? moment(item.end_date).format("DD/MM/YYYY, HH:mm") 
        : "N/A",
      pickup: item.pickup_location || "N/A",   // future me agar object aaye toh .name use karna
      dropoff: item.drop_location || "N/A",
      payment: item.payment_status === "Paid" ? "Success" : (item.payment_status || "N/A"),
      status: item.status || "N/A",  // Add status field
      transactionId: item.transaction_id || "N/A",  // backend se missing hai
      paymentMethod: item.payment_method || "N/A",  // backend se missing hai
      baseFare: item.base_price_per_day || "N/A",
      extraKms: item.extra_kms ?? "N/A",
      discount: item.discount_amount ?? "0.00",
      taxes: item.tax_amount ?? "0.00",
      amountpaid: item.total_amount ?? "0.00",
      
    };
  });
};


const CarBooking = () => { 
    const [globalFilter, setGlobalFilter] = useState("");
    const [pageIndex, setPageIndex] = useState(1);
    const pageSize = 10;
    const [activeTab, setActiveTab] = useState("upcoming");
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const[pageData,setPageData]=useState("")

    const abortRef = useRef(null);
    const debounceTimeoutRef = useRef(null);

    const fetchBookings = async ({ start = startDate, end = endDate, isSearch = false }) => {
      try {
        if (isSearch) {
          setLoading(false);
        } else {
          setLoading(true);
        }
        
        setError("");
        const items = await getBookingList({ 
          search: globalFilter, 
          start_date: start, 
          end_date: end, 
          page: pageIndex,
          type: activeTab 
        });
        const normalizedData = normalizeBookings(items?.data?.data);
        setPageData(items?.data?.page_data)
        setBookings(normalizedData || []);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(e.message || 'Failed to load bookings');
          setBookings([]);
        }
      } finally {
        setLoading(false);
      }
    };

    // Debounced effect for globalFilter changes
    useEffect(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      debounceTimeoutRef.current = setTimeout(() => {
        fetchBookings({ start: startDate, end: endDate, isSearch: true });
      }, 500);
      
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, [globalFilter]);

    // Effect for page changes
    useEffect(() => {
      fetchBookings({ start: startDate, end: endDate });
    }, [pageIndex, activeTab]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (abortRef.current) {
          abortRef.current.abort();
        }
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    const handleStartDate = (date) => {
      setStartDate(moment(date).format("YYYY-MM-DD"));
    };

    const handleEndDate = (date) => {
      setEndDate(moment(date).format("YYYY-MM-DD"));
    };

    const searchBookings = () => {
      fetchBookings({ search: globalFilter, start: startDate, end: endDate });
    };

    const handleDownloadInvoice = async (bookingId) => {
      try {
        setLoading(true);
        const response = await downloadBookingInvoice('bookingId');
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `invoice-${bookingId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        ToastMsg('Invoice downloaded successfully', 'success');
      } catch (error) {
        console.error('Error downloading invoice:', error);
        ToastMsg(error?.response?.data?.message || 'Failed to download invoice', "error");
      } finally {
        setLoading(false);
      }
    };

    const columns = useMemo(
      () => [
        { key: "id", label: "Id", minWidth: "36px", width: "36px", showSortIcon:false},
        { key: "bookingId", label: "BOOKING ID", minWidth: "150px", width: "150px", showSortIcon: false },
        { key: "name", label: "NAME", minWidth: "150px", width: "150px", showSortIcon: false },
        { key: "email", label: "EMAIL", minWidth: "180px", width: "180px", showSortIcon: false },
        { key: "phone", label: "PHONE NUMBER", minWidth: "180px", width: "180px", showSortIcon: false },
        { key: "carModel", label: "CAR MODEL", minWidth: "180px", width: "180px", showSortIcon: false },
        { key: "tripStart", label: "TRIP START DATE & TIME", minWidth: "250px", width: "250px", showSortIcon: false },
        { key: "tripEnd", label: "TRIP END DATE & TIME", minWidth: "250px", width: "250px", showSortIcon: false },
        { key: "pickup", label: "PICKUP LOCATION", minWidth: "190px", width: "190px", showSortIcon: false },
        { key: "dropoff", label: "DROP-OFF LOCATION", minWidth: "210px", width: "210px", showSortIcon: false },
        { 
          key: "payment", 
          label: "PAYMENT", 
          minWidth: "100px", 
          width: "100px", 
          render: (value) => (
            <span
              className={
                value === "Success" || value === "success"
                  ? "px-3 py-1 rounded-full text-xs font-normal bg-gradient-to-b from-[#0EBC651A] to-[#0EBC6500] text-[#0EBC65]"
                  : value === "Failed" || value === "failed"
                  ? "px-3 py-1 rounded-full text-xs font-normal bg-gradient-to-b from-[#FF56301A] to-[#FF563000] text-[#FF5630]"
                  : value === "Cancelled" || value === "cancelled"
                  ? "px-3 py-1 rounded-full text-xs font-normal bg-gradient-to-b from-[#F3B7531A] to-[#F3B75300] text-[#F3B753]"
                  : "px-3 py-1 rounded-full text-xs font-normal bg-gradient-to-b from-[#A0A0A01A] to-[#A0A0A000] text-[#A0A0A0]"
              }
            >
              {value}
            </span>
          ),
          showSortIcon: true 
        },
        { key: "transactionId", label: "TRANSACTION ID", minWidth: "190px", width: "190px", showSortIcon: true },
        { key: "paymentMethod", label: "PAYMENT METHOD", minWidth: "190px", width: "190px", showSortIcon: true },
        { key: "baseFare", label: "BASE FARE", minWidth: "190px", width: "190px", showSortIcon: true },
        { key: "extraKms", label: "EXTRA KMS", minWidth: "190px", width: "190px", showSortIcon: true },
        { key: "discount", label: "DISCOUNT", minWidth: "100px", width: "100px", showSortIcon: true },
        { key: "taxes", label: "TAXES", minWidth: "80px", width: "80px", showSortIcon: true },
        { key: "amountpaid", label: "TOTAL AMOUNT PAID", minWidth: "210px", width: "210px", showSortIcon: true },
        { 
          key: "download", 
          label: "DOWNLOAD INVOICE", 
          minWidth: "150px", 
          width: "150px", 
          showSortIcon: false,
          render: (value, row) => (
            <button
              onClick={() => handleDownloadInvoice(row.id)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 border ${
                row?.status === "Pending" || row?.status === "Cancelled"
                  ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                  : "bg-gradient-to-b from-[#F3B7531A] to-[#F3B75300] text-[#F3B753] hover:from-[#F3B7532A] hover:to-[#F3B7531A] border-[#F3B753] hover:border-[#F3B753]"
              }`}
              disabled={loading || row?.status === "Pending" || row?.status === "Cancelled"}
            >
              {loading ? 'Downloading...' : 'Download'}
            </button>
          )
        },
     
      ],
      [pageIndex, pageSize]
    );

   


    if (loading) {
      return <Loader />;
    }

    return (
      <div className="flex flex-col gap-4 p-8 bg-[#161616] min-h-screen">
        <Stats 
          handleStartDate={handleStartDate} 
          handleEndDate={handleEndDate} 
          handlegetData={searchBookings} 
          startDate={startDate} 
          endDate={endDate} 
          showtiles={false}
          pageData={pageData}
        />
        
        {/* Tabs */}
        <div className="flex gap-8 mb-4 border-b border-[#232323] w-fit">
          <button
            className={`px-2 pb-2 text-base font-semibold focus:outline-none transition-all ${activeTab === "upcoming" ? "text-[#F3B753] border-b-2 border-[#F3B753]" : "text-[#bdbdbd]"}`}
            onClick={() => { setActiveTab("upcoming"); setPageIndex(1); }}
          >
            Upcoming
          </button>
          <button
            className={`px-2 pb-2 text-base font-semibold focus:outline-none transition-all ${activeTab === "past" ? "text-[#F3B753] border-b-2 border-[#F3B753]" : "text-[#bdbdbd]"}`}
            onClick={() => { setActiveTab("past"); setPageIndex(1); }}
          >
            Past
          </button>
        </div>

        <div className="flex items-center gap-4">
          {loading && <span className="text-xs text-[#bdbdbd]">Loading...</span>}
          {error && <span className="text-xs text-red-400">{error}</span>}
        </div>

        <div className="rounded-[8px] w-full">
          <CustomTable
            data={bookings}
            columns={columns}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            pageSize={pageSize}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            fetchUsers={searchBookings}
            disableClientFilter={true}
            pageData={pageData}
          />
        </div>
      </div>
    );
}

export default CarBooking
